/**
 * The Account group's own pages, and the one way to link to them.
 *
 * Each page is reached from a chip on Account, and coming back lands focus on
 * that chip: the chip wears the page's slug as `data-focus-key`, and the page's
 * way back carries the same slug as `returnTo` (`SettingsScreen.tsx`). Two
 * strings that have to match were written by hand at every link, so both come
 * from here, typed by the slug.
 */

export const ACCOUNT_PATH = '/settings/account';

/**
 * The link in the password reset email, `?token=` and all. Rails writes it
 * (`Api::PasswordResetMailer`), so it is a public address that must keep
 * working; the app sends it on to the new-password page with its token.
 */
export const RESET_LINK_PATH = '/reset-password';

/** Declared once: the type is read off the list, so the route guard cannot miss a page. */
const SUB_PAGES = [
  'register',
  'sign-in',
  'forgot-password',
  'new-password',
  'need-name',
  'need-avatar',
  'avatar',
  'delete',
] as const;

export type AccountSubPage = (typeof SUB_PAGES)[number];

export function isAccountSubPage(page: string): page is AccountSubPage {
  return (SUB_PAGES as readonly string[]).includes(page);
}

export function accountPath(page: AccountSubPage): string {
  return `${ACCOUNT_PATH}/${page}`;
}

/** A chip's link to one of the pages: where it goes, and the key focus comes back to. */
export function accountPageLink(page: AccountSubPage): {
  readonly to: string;
  readonly 'data-focus-key': AccountSubPage;
} {
  return { to: accountPath(page), 'data-focus-key': page };
}

/** The state a way back to Account carries, so focus lands on the chip that opened the page. */
export function returnToChip(page: AccountSubPage): { readonly returnTo: AccountSubPage } {
  return { returnTo: page };
}

/**
 * Profile also opens the delete page, and a player who keeps their account goes
 * back to Profile rather than to Settings (`docs/ux/account/delete-account.md`:
 * "Cancel returns to the invoking Settings or Profile control"). The link says
 * so in its state, and the page's ways back read it here.
 */
export const PROFILE_PATH = '/profile';

export function fromProfileState(): { readonly origin: 'profile' } {
  return { origin: 'profile' };
}

export function openedFromProfile(state: unknown): boolean {
  return (state as { origin?: unknown } | null)?.origin === 'profile';
}

/**
 * A path inside the app, or nothing. A path read from router state is only
 * followed when it stays on the site: `/play`, never `//elsewhere` or a URL.
 */
export function appPath(value: unknown): string | undefined {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')
    ? value
    : undefined;
}

/**
 * Where a sign-in or a new account goes once it is done, when that is not
 * Account: the page a guest left to make one, such as the offer
 * (`docs/ux/commerce/purchase.md`: "preserve the offer and origin through
 * that step"). `state` is that page's own router state, its `backTo` and all,
 * so the page comes back exactly as it was left.
 */
export interface Resume {
  readonly to: string;
  readonly state?: Record<string, unknown> | undefined;
}

/** The router state a Sign in link carries so the account comes back to `to`. */
export function resumeState(
  to: string,
  state?: Record<string, unknown>,
): { readonly resume: Resume } {
  return { resume: { to, state } };
}

/** The way back a sign-in page was handed, or null. One that leaves the app is ignored. */
export function resumeOf(state: unknown): Resume | null {
  const resume = (state as { resume?: unknown } | null)?.resume;
  if (typeof resume !== 'object' || resume === null) return null;
  const { to, state: carried } = resume as { to?: unknown; state?: unknown };
  const path = appPath(to);
  if (path === undefined) return null;
  return {
    to: path,
    state:
      typeof carried === 'object' && carried !== null
        ? (carried as Record<string, unknown>)
        : undefined,
  };
}

/** Hand a way back on from one sign-in page to the next: Sign in to Create an account. */
export function carryResume(state: unknown): { readonly resume: Resume } | undefined {
  const resume = resumeOf(state);
  return resume === null ? undefined : { resume };
}
